import React, { useEffect } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Whoops, WhoopsReason } from 'components/whoops';
import { getLogger } from 'utils/datadog-logger';
import { ERROR_BOUNDARY_LOGGING_KILL_SWITCH_FLAG } from 'utils/analytics/constants/feature-flags';
import { GraphqlError, RouteParamsError, ProtectedRouteError, PageNotFoundError, LoadFailureError } from './errors';

const logger = getLogger('react-error-boundary');
const MAX_STACK_LENGTH = 1024;
const loggedErrorSignatures = new Set<string>();

export const resetLoggedErrors = (): void => {
  loggedErrorSignatures.clear();
};

function getErrorSignature(error: Error, pathname = ''): string {
  const stack = error.stack ?? '';
  const raw = `${error.name}:${error.message}:${stack}:${pathname}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = (hash * 31 + raw.charCodeAt(i)) % 2147483647;
  }
  return String(hash);
}

const getErrorReason = (error: Error): WhoopsReason => {
  if (error instanceof RouteParamsError || error instanceof ProtectedRouteError || error instanceof PageNotFoundError) {
    return WhoopsReason.pageNotFound;
  }

  if (error instanceof GraphqlError) {
    return WhoopsReason.serverError;
  }

  if (error instanceof LoadFailureError) {
    return WhoopsReason.loadError;
  }

  return WhoopsReason.serverError;
};

type UseErrorFallbackReturn = {
  reason: WhoopsReason;
};

function useErrorFallback({ error }: FallbackProps): UseErrorFallbackReturn {
  const flags = useFlags();
  const isKillSwitchActive = Boolean(flags[ERROR_BOUNDARY_LOGGING_KILL_SWITCH_FLAG]);
  const reason = getErrorReason(error);

  useEffect(() => {
    console.error(error);

    try {
      if (isKillSwitchActive) {
        return;
      }

      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const signature = getErrorSignature(error, pathname);
      if (loggedErrorSignatures.has(signature)) {
        return;
      }
      loggedErrorSignatures.add(signature);

      const stack = typeof error.stack === 'string' ? error.stack.slice(0, MAX_STACK_LENGTH) : undefined;

      logger.error(error.message, {
        error: {
          name: error.name,
          message: error.message,
          stack,
        },
        reason,
        pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
        search: typeof window !== 'undefined' ? window.location.search : undefined,
        referrer: typeof document !== 'undefined' ? document.referrer : undefined,
        dispensaryId: typeof window !== 'undefined' ? window.reactEnv?.dispensaryId : undefined,
        enterpriseId: typeof window !== 'undefined' ? window.reactEnv?.enterpriseId : undefined,
        host: typeof window !== 'undefined' ? window.location.host : undefined,
      });
    } catch {
      // Intentionally swallowed so logging never causes a secondary crash inside the ErrorBoundary
    }
  }, [error, isKillSwitchActive, reason]);

  return {
    reason,
  };
}

export function ErrorFallback(props: FallbackProps): JSX.Element {
  const { reason } = useErrorFallback(props);

  return <Whoops reason={reason} />;
}
